import React, { useEffect, useCallback, useMemo, useState } from "react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useFormContext } from "react-hook-form";
import { LineItem, MoverFee, Material } from "@/types";
import { BoxIcon, DollarSignIcon, Trash2Icon, TruckIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  FormField,
  FormItem,
  FormControl,
  FormLabel,
} from "@/components/ui/form";
import { useSelector } from "react-redux";
import { getSelectedMover } from "@/store/mover/slice";
import { addressBlank, cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { computeHourlyRate } from "./functions";
import { checkMobileToken } from "@/store/session/slice";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import MaterialsSelector from "./MaterialsSelector";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLink } from "@fortawesome/free-solid-svg-icons";
import { Label } from "@/components/ui/label";

export default function LineItems({
  mode = "edit",
  buttonsPosition = "top",
}: {
  mode?: "edit" | "invoice";
  buttonsPosition?: "top" | "bottom";
}) {
  const selectedMover = useSelector(getSelectedMover);
  const inApp = useSelector(checkMobileToken);
  const { control, watch, setValue, getValues } = useFormContext();
  const [selectedFee, setSelectedFee] = React.useState<string>("");
  const waypoints = watch("waypoints_info");
  const numOfMovers = watch("num_of_movers");
  const lineItems = watch("line_items");
  const origin = watch("origin");
  const destination = watch("destination");
  const [materialsOpen, setMaterialsOpen] = useState(false);

  const ddt = useMemo(() => {
    return lineItems.find(
      (item: LineItem) => item.name === "CA Double Drive Time"
    );
  }, [lineItems]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (
        name &&
        name.startsWith("line_items.") &&
        (name.endsWith(".price") ||
          name.endsWith(".qty") ||
          name.endsWith(".discount"))
      ) {
        const [, index] = name.split(".");
        // const [, index, field] = name.split(".");
        const lineItem = value.line_items[parseInt(index)];
        const price = parseFloat(lineItem.price) || 0;
        const discount = parseFloat(lineItem.discount) || 0;
        const qty = parseFloat(lineItem.qty) || 0;
        const subtotal = (price - discount) * qty;

        setValue(`line_items.${index}.subtotal`, subtotal.toFixed(2));
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  const hideQuantityInput = (item: LineItem) => {
    if (!selectedMover) {
      return false;
    }

    return (
      item.category === "labor" &&
      mode === "edit" &&
      !selectedMover?.mover_configuration.estimates_show_time_and_total
    );
  };

  const calculateEstimatedTotal = () => {
    return lineItems.reduce((acc: number, item: LineItem) => {
      return acc + parseFloat(item.subtotal?.toString() || "0");
    }, 0);
  };

  const addOrUpdateDDT = useCallback(
    (add: boolean) => {
      if (
        !selectedMover ||
        !waypoints ||
        waypoints.length === 0 ||
        !numOfMovers ||
        numOfMovers === 0
      ) {
        return;
      }

      const lineItems = watch("line_items");
      const ddt = lineItems.find(
        (item: LineItem) => item.name === "CA Double Drive Time"
      );

      // marked for destroy but add it back again
      if (ddt && ddt._destroy && add) {
        const ddtIndex = lineItems.findIndex(
          (item: LineItem) => item.name === "CA Double Drive Time"
        );

        if (ddtIndex !== -1) {
          const filteredLineItems = lineItems.filter(
            (_: LineItem, index: number) => index !== ddtIndex
          );

          // Get the DDT item without the _destroy flag
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { _destroy, ...ddtWithoutDestroy } = lineItems[ddtIndex];

          // Append the DDT item to the end of the array
          setValue("line_items", [...filteredLineItems, ddtWithoutDestroy]);
        }

        return;
      }

      const driveTimeMins = waypoints
        .slice(1, -1)
        .reduce((acc: number, seg: any) => acc + seg.duration, 0);

      // Round up to the nearest 15 minutes
      const doubleDriveTime = Math.ceil(driveTimeMins / 15) * 15;
      const doubleDriveTimeHrs = parseFloat((doubleDriveTime / 60).toFixed(2));
      const numMovers = parseInt(numOfMovers?.toString() || "0");
      const serviceDate = watch("service_date");
      const rate = computeHourlyRate(
        numMovers,
        selectedMover.labor_rates,
        serviceDate
      );

      const currentLineItems = watch("line_items") || [];
      const existingDDT = currentLineItems.find(
        (item: LineItem) => item.name === "CA Double Drive Time"
      );

      if (existingDDT) {
        const existingIndex = currentLineItems.findIndex(
          (item: LineItem) => item.name === "CA Double Drive Time"
        );

        if (existingIndex !== -1) {
          const updatedLineItems = [...currentLineItems];
          updatedLineItems[existingIndex] = {
            ...updatedLineItems[existingIndex],
            qty: doubleDriveTimeHrs,
            price: rate,
            subtotal: rate * doubleDriveTimeHrs,
          };

          setValue("line_items", updatedLineItems);
        }
      } else {
        const newLineItem = {
          name: "CA Double Drive Time",
          price: rate,
          discount: 0,
          qty: doubleDriveTimeHrs,
          subtotal: rate * doubleDriveTimeHrs,
          nature: "charge",
          category: "flat",
        };
        setValue("line_items", [...currentLineItems, newLineItem]);
      }
    },
    [selectedMover, waypoints, numOfMovers]
  );

  useEffect(() => {
    const ddt = lineItems.find(
      (item: LineItem) => item.name === "CA Double Drive Time"
    );

    if (!ddt) {
      return;
    }

    if (ddt._destroy) {
      return;
    }
  }, [addOrUpdateDDT, waypoints, numOfMovers]);

  if (!selectedMover) {
    return null;
  }

  const addMoverFee = (feeId: string) => {
    const fee = selectedMover?.mover_fees.find(
      (f: MoverFee) => f.id === parseInt(feeId, 10)
    );
    if (fee) {
      const newLineItem = {
        name: fee.name,
        price: fee.amount,
        discount: 0,
        qty: 1,
        subtotal: fee.amount ? fee.amount : null,
        mover_fee_id: fee.id,
        type: "mover",
        description: fee.description,
        nature: "charge",
        category: "flat",
      };
      const currentLineItems = watch("line_items") || [];
      setValue("line_items", [...currentLineItems, newLineItem]);
      setSelectedFee(""); // Reset the selection
    }
  };

  const removeLineItem = (index: number) => {
    const currentLineItems = watch("line_items") || [];
    const lineItem = currentLineItems[index];

    if (!lineItem.id) {
      // For new line items, remove them completely
      setValue(
        "line_items",
        currentLineItems.filter((_: LineItem, i: number) => i !== index)
      );
    } else {
      // For existing line items, mark as destroyed
      const updatedLineItems = currentLineItems.map(
        (item: LineItem, i: number) =>
          i === index ? { ...item, _destroy: true } : item
      );
      setValue("line_items", updatedLineItems);
    }
  };

  const addDiscountItem = () => {
    const currentLineItems = getValues("line_items") || [];
    const newLineItem = {
      name: "Discount",
      price: null,
      discount: 0,
      qty: 1,
      subtotal: null,
      type: "custom",
      description: null,
      nature: "discount",
      category: "flat",
    };

    const newIndex = currentLineItems.length;
    setValue("line_items", [...currentLineItems, newLineItem]);

    // Set a timeout to focus the price input after the component re-renders
    setTimeout(() => {
      const priceInput = document.querySelector(
        `input[name="line_items.${newIndex}.price"]`
      );
      if (priceInput) {
        (priceInput as HTMLInputElement).focus();
      }
    }, 0);
  };

  const calculateSubTotal = (price: number, qty: number, discount: number) => {
    if (price && qty) {
      return (price - discount) * qty;
    }

    return 0;
  };

  const renderSubTotal = (index: number) => {
    const price = parseFloat(watch(`line_items.${index}.price`) || "0");
    const qty = parseFloat(watch(`line_items.${index}.qty`) || "0");
    const discount = parseFloat(watch(`line_items.${index}.discount`) || "0");

    if (!qty) {
      return <span>-</span>;
    }

    return (
      <div className="flex flex-col items-end gap-0.5">
        {discount > 0 && (
          <span className={cn(discount > 0 && "line-through text-red-600")}>
            {Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(Math.abs(calculateSubTotal(price, qty, 0)))}
          </span>
        )}
        <span>
          {Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
          }).format(calculateSubTotal(price, qty, discount))}
        </span>
      </div>
    );
  };

  const addMaterials = (
    materials: Array<{ material: Material; price?: number; quantity?: number }>
  ) => {
    const currentLineItems = watch("line_items") || [];
    const newLineItems = [...currentLineItems];

    materials.forEach((item) => {
      // Check if material already exists in line items
      const existingIndex = currentLineItems.findIndex(
        (lineItem: LineItem) =>
          lineItem.name + lineItem.description ===
            item.material.name + item.material.description &&
          lineItem.category === "materials"
      );

      const newLineItem = {
        name: item.material.name,
        price: item.price || item.material.price,
        discount: 0,
        qty: item.quantity || 1,
        subtotal: (item.price || item.material.price) * (item.quantity || 1),
        type: "materials",
        description: item.material.description,
        nature: "charge",
        category: "materials",
        _destroy: undefined,
      };

      if (existingIndex !== -1) {
        // Update existing line item
        newLineItems[existingIndex] = {
          ...newLineItems[existingIndex],
          ...newLineItem,
        };
      } else {
        // Add new line item
        newLineItems.push(newLineItem);
      }
    });

    setValue("line_items", [...newLineItems]);
  };

  const renderButtons = () => {
    return (
      <div className="flex flex-row items-start gap-4">
        <Select
          value={selectedFee}
          onValueChange={(value) => {
            if (value === "materials") {
              // Sheet will handle this
              return;
            }
            if (value !== "custom") {
              addMoverFee(value);
            } else {
              const currentLineItems = watch("line_items") || [];
              setValue("line_items", [
                ...currentLineItems,
                {
                  name: "",
                  price: null,
                  discount: 0,
                  qty: 1,
                  subtotal: null,
                  type: "custom",
                  description: null,
                  nature: "charge",
                  category: "flat",
                },
              ]);
            }
          }}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Add Line Item" />
          </SelectTrigger>
          <SelectContent>
            {[...selectedMover.mover_fees]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((fee: MoverFee) => (
                <SelectItem key={fee.id} value={fee.id.toString()}>
                  {fee.name}
                </SelectItem>
              ))}
            <SelectItem value="custom">Add Custom Fee</SelectItem>
          </SelectContent>
        </Select>
        <Sheet open={materialsOpen} onOpenChange={setMaterialsOpen}>
          <SheetTrigger asChild>
            <Button
              type="button"
              variant="secondary"
              className="border text-sm font-normal border-gray-400 flex items-center gap-1"
            >
              <BoxIcon className="w-3 h-3" />
              Add Materials
            </Button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="w-full md:max-w-[600px] max-h-screen overflow-y-auto"
          >
            <SheetHeader>
              <SheetTitle>Add Materials</SheetTitle>
              <SheetDescription className="text-sm text-gray-800">
                Manage materials on the{" "}
                <Link
                  to="/mover-settings/materials"
                  className="underline font-semibold"
                  target="_blank"
                >
                  Materials Settings
                </Link>{" "}
                <sup>
                  <FontAwesomeIcon icon={faExternalLink} className="w-2 h-2" />
                </sup>{" "}
                page. Please reload this page to see updates.
              </SheetDescription>
            </SheetHeader>
            <MaterialsSelector
              onAdd={addMaterials}
              currentLineItems={watch("line_items") || []}
              onClose={() => setMaterialsOpen(false)}
            />
          </SheetContent>
        </Sheet>
        {selectedMover.state.toUpperCase() === "CA" &&
          !addressBlank(origin) &&
          !addressBlank(destination) &&
          (!ddt || ddt._destroy) && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="border text-sm font-normal border-gray-400 flex items-center gap-1"
              onClick={() => addOrUpdateDDT(true)}
            >
              <TruckIcon className="w-3 h-3" />
              Add Double Drive Time
            </Button>
          )}

        <Button
          type="button"
          size="sm"
          variant="secondary"
          className={cn(
            "border text-sm font-normal border-gray-400 flex items-center gap-1",
            mode === "invoice" && "hidden"
          )}
          onClick={addDiscountItem}
        >
          <DollarSignIcon className="w-3 h-3" />
          Add Discount
        </Button>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {buttonsPosition === "top" && renderButtons()}
      <div id="billing-table">
        {/* add a shadcn/ui dropdown menu here that shows all the mover fees and allows you to select one. when you select one, add a new line item to the line_items array with the fee name as line item name, amount as line item price, qty set to 1, and mover_fee_id set to the id of the fee you selected.  */}
        <div
          className={cn(
            "grid grid-cols-6 gap-2 mb-4 border-t border-b border-stone-300 py-0.5",
            inApp && "hidden"
          )}
        >
          <div className="py-1 text-sm text-gray-900 font-semibold col-span-2">
            Item
          </div>
          <div className="py-1 text-sm text-gray-900 font-semibold col-span-1">
            Price
          </div>
          <div className="py-1 text-sm text-gray-900 font-semibold col-span-1">
            Quantity
          </div>
          <div className="py-1 text-sm text-gray-900 font-semibold col-span-1">
            Price Discount
          </div>
          <div className="py-1 text-sm text-gray-900 font-semibold col-span-1 text-right">
            Total&nbsp;&nbsp;&nbsp;&nbsp;
          </div>
        </div>

        {watch("line_items")
          // .filter((item: LineItem) => item.category !== "materials")
          .map((item: LineItem, index: number) => (
            <div
              key={item.name || index}
              className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-3 flex items-center"
            >
              <div className="col-span-1 md:col-span-2">
                <FormField
                  control={control}
                  name={`line_items.${index}.name`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        {item.type === "custom" ? (
                          <Input
                            {...field}
                            className="w-auto"
                            value={field.value ?? ""}
                            onChange={(e) => {
                              field.onChange(e.target.value);
                            }}
                            autoFocus
                          />
                        ) : (
                          <div className="flex items-center gap-1 text-base md:text-sm font-semibold md:font-normal">
                            <span
                              className={cn(item._destroy && "line-through")}
                            >
                              {field.value}
                            </span>
                            {item._destroy && (
                              <span className="text-xs text-red-600 font-medium">
                                (pending removal)
                              </span>
                            )}
                            {item.nature === "discount" && (
                              <span className="ml-2 text-xs text-green-600 font-medium">
                                (discount)
                              </span>
                            )}
                          </div>
                        )}
                      </FormControl>
                    </FormItem>
                  )}
                />
                {item.description && (
                  <div className="text-sm text-gray-600 font-normal">
                    {item.description}
                  </div>
                )}
              </div>
              <div className="col-span-1 md:col-span-1">
                <FormField
                  control={control}
                  name={`line_items.${index}.price`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={cn("hidden", inApp && "block")}>
                        Rate{" "}
                        {item.category === "labor" && (
                          <span className="text-sm">(per hour)</span>
                        )}
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-700 text-sm">
                            $
                          </span>
                          <Input
                            className="pl-6 w-full md:w-24"
                            value={
                              item.nature === "discount"
                                ? field.value
                                  ? field.value
                                  : ""
                                : field.value ?? ""
                            }
                            onChange={(e) => {
                              const value = e.target.value;

                              // Allow empty string, numbers, one decimal point, and up to 2 decimal places. Also allow just a decimal point temporarily
                              if (
                                value === "" ||
                                value === "." ||
                                /^\d*\.?\d{0,2}$/.test(value)
                              ) {
                                field.onChange(value);
                              }
                            }}
                            onBlur={(e) => {
                              // On blur, convert to number
                              const value = e.target.value;
                              if (value === "" || value === ".") {
                                field.onChange("");
                              } else {
                                const numValue = parseFloat(value);
                                if (item.nature === "discount") {
                                  field.onChange(
                                    numValue ? -Math.abs(numValue) : numValue
                                  );
                                } else {
                                  field.onChange(numValue);
                                }
                              }
                            }}
                            onFocus={(e) => e.target.select()}
                          />
                          {/* {item.category === "labor" && (
                          <span className="absolute right-10 top-1/2 transform -translate-y-1/2 text-black text-sm">
                            /hr
                          </span>
                        )} */}
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <div className="col-span-1 md:col-span-1">
                <FormField
                  control={control}
                  name={`line_items.${index}.qty`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel
                        className={cn("hidden", inApp && "block flex gap-1")}
                      >
                        Quantity{" "}
                        {item.category === "labor" && (
                          <span className="text-sm">(hours)</span>
                        )}
                      </FormLabel>
                      <FormControl>
                        {hideQuantityInput(item) ? (
                          <div className="text-sm text-stone-700">TBD</div>
                        ) : (
                          <Input
                            className="w-full md:w-20"
                            type="number"
                            step="0.01"
                            {...field}
                            value={field.value ?? ""}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value === ""
                                  ? ""
                                  : parseFloat(e.target.value)
                              )
                            }
                            onFocus={(e) => e.target.select()}
                          />
                        )}
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <div
                className={cn("col-span-1 md:col-span-1", inApp && "hidden")}
              >
                <FormField
                  control={control}
                  name={`line_items.${index}.discount`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          disabled={
                            watch(`line_items.${index}.nature`) === "discount"
                          }
                          className="w-24"
                          type="number"
                          step="0.01"
                          {...field}
                          value={field.value ?? ""}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value === ""
                                ? ""
                                : parseFloat(e.target.value)
                            )
                          }
                          onFocus={(e) => e.target.select()}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <div className="col-span-1 md:col-span-1 flex items-center justify-end">
                <div
                  className={cn(
                    "text-right text-base md:text-sm font-medium space-x-2",
                    item._destroy && "line-through"
                  )}
                >
                  {renderSubTotal(index)}
                </div>
                <div>
                  {(mode === "edit" && index !== 0) ||
                  (mode === "invoice" && !item.id) ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="p-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (
                          window.confirm(
                            "Are you sure you want to remove this item?"
                          )
                        ) {
                          removeLineItem(index);
                        }
                      }}
                    >
                      <Trash2Icon className="w-6 h-6 md:w-5 md:h-5 text-red-700" />
                    </Button>
                  ) : (
                    <div className="p-2">
                      <Trash2Icon
                        className="w-6 h-6 md:w-5 md:h-5 text-stone-300 cursor-not-allowed"
                        onClick={() => alert("This item cannot be removed")}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

        {/* {watch("line_items").filter(
          (item: LineItem) => item.category === "materials"
        ).length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-3 flex items-center text-sm">
            <div className="col-span-1 md:col-span-2">
              <div
                className="underline underline-offset-4 cursor-pointer mb-1"
                onClick={() => setMaterialsOpen(true)}
              >
                Materials
              </div>
              {watch("line_items")
                .filter((item: LineItem) => item.category === "materials")
                .map((item: LineItem) => (
                  <div className="text-xs text-stone-600" key={item.name}>
                    {item.name} {item.qty} x{" "}
                    {Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(item.price || 0)}{" "}
                    ={" "}
                    {Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(item.subtotal || 0)}
                  </div>
                ))}
            </div>
            <div className="col-span-1 md:col-span-1">Multiple</div>
            <div className="col-span-1 md:col-span-1">
              {
                watch("line_items").filter(
                  (item: LineItem) => item.category === "materials"
                ).length
              }{" "}
              items
            </div>
            <div className="col-span-1 md:col-span-1"></div>
            <div className="text-right font-medium">
              {Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(
                watch("line_items")
                  .filter((item: LineItem) => item.category === "materials")
                  .reduce(
                    (acc: number, item: LineItem) =>
                      acc + parseFloat(item.subtotal?.toString() || "0"),
                    0
                  )
              )}
            </div>
          </div>
        )} */}

        {selectedMover?.mover_configuration.estimates_show_time_and_total && (
          <div className="my-3 text-sm font-medium border-t border-b border-stone-300 py-1.5 bg-stone-50 pr-4">
            <div className="flex items-center justify-end gap-4">
              <span>Total</span>{" "}
              <span>
                {Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(calculateEstimatedTotal())}
              </span>
            </div>
          </div>
        )}

        {mode === "edit" && (
          <div className="flex items-center justify-end gap-4">
            <Label>
              Not to Exceed Price{" "}
              <span className="text-gray-700 text-xs">(optional)</span>
            </Label>
            <FormField
              control={control}
              name="not_to_exceed_price"
              render={({ field }) => (
                <FormItem className="w-32">
                  <FormControl>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-700 text-sm">
                        $
                      </span>
                      <Input
                        className="pl-6 w-full"
                        value={field.value ?? ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow empty string, numbers, one decimal point, and up to 2 decimal places
                          if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
                            field.onChange(value);
                          }
                        }}
                        onBlur={(e) => {
                          // On blur, convert to number or empty string
                          const value = e.target.value;
                          field.onChange(value === "" ? "" : parseFloat(value));
                        }}
                        onFocus={(e) => e.target.select()}
                      />
                    </div>
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        )}

        {buttonsPosition === "bottom" && renderButtons()}
        {/* <div className="mt-4 grid grid-cols-5 pt-4 font-bold text-right">
        <div className="col-span-4 text-right">Est. Total</div>
        <div className="col-span-1 text-right">${calculateTotal()}</div>
      </div> */}
      </div>
    </div>
  );
}
